import React, { useEffect, useId, useRef, useState } from 'react';
import { movdFocus, movdTokens } from '../design-system/movdTokens.js';
import { getApiBase } from '../utils/apiConfig.js';

const CanadianAddressAutocomplete = ({ value, onChange, onSelect, placeholder = 'Enter address', onBlur, style, sx, inputStyle, ...props }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [inputValue, setInputValue] = useState(value || '');
  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const cacheRef = useRef(new Map());
  const rootRef = useRef(null);
  const inputId = useId();
  const listId = `${inputId}-suggestions`;

  const searchAddresses = async query => {
    if (!query || query.length < 3) {
      abortRef.current?.abort();
      setSuggestions([]);
      setOpen(false);
      setLoading(false);
      return;
    }
    const cacheKey = query.trim().toLocaleLowerCase('en-CA');
    const cached = cacheRef.current.get(cacheKey);
    if (cached) {
      setSuggestions(cached);
      setOpen(cached.length > 0);
      setActiveIndex(-1);
      return;
    }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      setLoading(true);
      const response = await fetch(`${getApiBase()}/geocode?query=${encodeURIComponent(query)}`, { signal: controller.signal });
      if (!response.ok) throw new Error('address-search-failed');
      const data = await response.json();
      if (controller.signal.aborted) return;
      const mappedSuggestions = data.map(item => ({ display_name: item.display_name, lat: item.lat, lon: item.lon, place_id: item.place_id, type: item.type, address: item.address }));
      cacheRef.current.set(cacheKey, mappedSuggestions);
      setSuggestions(mappedSuggestions);
      setOpen(mappedSuggestions.length > 0);
      setActiveIndex(-1);
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('[CanadianAddressAutocomplete] Search error:', error);
      setSuggestions([]);
      setOpen(false);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  };

  useEffect(() => setInputValue(value || ''), [value]);
  useEffect(() => {
    const handlePointerDown = event => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, []);
  useEffect(() => () => {
    clearTimeout(debounceRef.current);
    abortRef.current?.abort();
  }, []);

  const selectSuggestion = suggestion => {
    setInputValue(suggestion.display_name);
    onChange?.(suggestion.display_name);
    onSelect?.(suggestion);
    setOpen(false);
    setActiveIndex(-1);
  };

  const handleKeyDown = event => {
    if (!open || !suggestions.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex(index => (index + 1) % suggestions.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex(index => (index - 1 + suggestions.length) % suggestions.length);
    } else if (event.key === 'Enter' && activeIndex >= 0) {
      event.preventDefault();
      selectSuggestion(suggestions[activeIndex]);
    } else if (event.key === 'Escape') {
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  return (
    <div ref={rootRef} style={{ position: 'relative', width: '100%', ...style }}>
      <div style={{ position: 'relative' }}>
        <input
          id={inputId}
          {...props}
          value={inputValue}
          placeholder={placeholder}
          autoComplete="off"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-activedescendant={activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
          onChange={event => {
            const nextValue = event.target.value;
            setInputValue(nextValue);
            onChange?.(nextValue);
            clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => searchAddresses(nextValue), 180);
          }}
          onFocus={() => suggestions.length && setOpen(true)}
          onBlur={onBlur}
          onKeyDown={handleKeyDown}
          style={{ width: '100%', minHeight: 56, boxSizing: 'border-box', padding: '0 48px 0 16px', border: `1px solid ${movdTokens.color.paperMuted}`, borderRadius: movdTokens.radius.control, background: movdTokens.color.paper, color: movdTokens.color.ink, font: 'inherit', fontSize: 16, fontWeight: 700, ...(sx?.native || {}), ...inputStyle }}
          onFocusCapture={event => { event.currentTarget.style.outline = movdFocus.outline; event.currentTarget.style.outlineOffset = `${movdFocus.outlineOffset}px`; }}
          onBlurCapture={event => { event.currentTarget.style.outline = ''; }}
        />
        <span aria-hidden="true" style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', color: loading ? movdTokens.color.warm : movdTokens.color.inkSoft, fontSize: 17 }}>{loading ? '…' : '⌖'}</span>
      </div>
      {open && suggestions.length > 0 && (
        <ul id={listId} role="listbox" style={{ position: 'absolute', zIndex: 1400, left: 0, right: 0, top: 'calc(100% + 8px)', margin: 0, padding: 6, listStyle: 'none', background: movdTokens.color.paper, border: `1px solid ${movdTokens.color.paperMuted}`, borderRadius: movdTokens.radius.control, boxShadow: '0 18px 42px rgba(0,0,0,.28)', maxHeight: 300, overflowY: 'auto' }}>
          {suggestions.map((suggestion, index) => (
            <li key={`${suggestion.place_id}-${index}`} id={`${listId}-${index}`} role="option" aria-selected={index === activeIndex}>
              <button type="button" onMouseDown={event => event.preventDefault()} onClick={() => selectSuggestion(suggestion)} style={{ width: '100%', padding: '12px 10px', border: 0, borderRadius: 7, background: index === activeIndex ? '#DCE8E4' : 'transparent', color: movdTokens.color.ink, textAlign: 'left', font: 'inherit', cursor: 'pointer' }}>
                <span style={{ display: 'block', color: movdTokens.color.tealSoft, fontSize: 12, marginBottom: 3 }}>LOCATION</span>
                <span style={{ fontSize: 14, fontWeight: 700 }}>{suggestion.display_name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CanadianAddressAutocomplete;
